import { useEffect, useState, useCallback } from "react";
import { Plus, Home, Clock, CheckCircle2, XCircle, ChevronRight, Video, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { prototypeApi } from "@/lib/prototypeApi";

function neutralStatus(p) {
  if (!p.listingId) return { key: "concept", label: "Concept", cls: "bg-re-soft text-re-muted" };
  if (p.listingStatus === "success") return { key: "done", label: "Voltooid", cls: "bg-emerald-100 text-emerald-700" };
  if (p.listingStatus === "error") return { key: "failed", label: "Mislukt", cls: "bg-red-100 text-re-red" };
  return { key: "processing", label: "In verwerking", cls: "bg-amber-100 text-amber-700" };
}

const title = (p) => p.draft?.address?.formattedAddress || p.address || "Naamloos videoproject";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-re-line bg-white px-5 py-4" data-testid={testid}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-re-red/10 text-re-red"><Icon className="h-5 w-5" /></span>
      <div>
        <div className="font-heading text-2xl font-bold text-re-charcoal">{value}</div>
        <div className="text-xs text-re-muted">{label}</div>
      </div>
    </div>
  );
}

export function ProjectsPage({ onNewProject, onOpenProject, reloadKey }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await prototypeApi.listProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects, reloadKey]);

  const stats = {
    total: projects.length,
    processing: projects.filter((p) => p.listingId && !["success", "error"].includes(p.listingStatus)).length,
    done: projects.filter((p) => p.listingStatus === "success").length,
    failed: projects.filter((p) => p.listingStatus === "error").length,
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-re-charcoal">Projecten</h1>
          <p className="mt-1 text-sm text-re-muted">Beheer je videoprojecten en start nieuwe woningen.</p>
        </div>
        <Button onClick={onNewProject} data-testid="new-project-button" className="rounded-full bg-re-red px-6 py-6 text-base font-semibold text-white hover:bg-re-deepred">
          <Plus className="mr-2 h-5 w-5" /> Nieuw videoproject
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Home} label="Projecten" value={stats.total} testid="stat-projects" />
        <Stat icon={Clock} label="In verwerking" value={stats.processing} testid="stat-processing" />
        <Stat icon={CheckCircle2} label="Voltooid" value={stats.done} testid="stat-done" />
        <Stat icon={XCircle} label="Mislukt" value={stats.failed} testid="stat-failed" />
      </div>

      <div>
        <h2 className="mb-3 font-heading text-lg font-semibold text-re-charcoal">Verwerkte woningen</h2>
        {loading ? (
          <p className="text-sm text-re-muted">Laden…</p>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="projects-error">
            <AlertTriangle className="mx-auto h-8 w-8 text-re-red" />
            <p className="mt-3 text-sm text-re-charcoal">Projecten konden niet worden geladen.</p>
            <Button variant="outline" onClick={fetchProjects} data-testid="projects-retry" className="mt-4 rounded-full">Opnieuw proberen</Button>
          </div>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-re-line bg-white p-10 text-center" data-testid="projects-empty">
            <Video className="mx-auto h-8 w-8 text-re-muted" />
            <p className="mt-3 text-sm text-re-muted">Nog geen projecten. Start je eerste videoproject.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="projects-list">
            {projects.map((p) => {
              const st = neutralStatus(p);
              return (
                <li key={p.localProjectId}>
                  <button type="button" onClick={() => onOpenProject(p)} data-testid={`project-row-${p.localProjectId}`}
                    className="flex w-full items-center justify-between rounded-2xl border border-re-line bg-white px-5 py-4 text-left transition-colors hover:border-re-red/40">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-re-charcoal">{title(p)}</p>
                      <p className="text-xs text-re-muted">{(p.draft?.photosMeta?.length ?? p.photoOrder?.length ?? 0)} foto&apos;s · bijgewerkt {new Date(p.updatedAt || Date.now()).toLocaleDateString("nl-NL")}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${st.cls}`}>{st.label}</span>
                      <ChevronRight className="h-4 w-4 text-re-muted" />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
